import { useEffect, useMemo, useState } from 'react';

import FormModal from '../../../Modals/FormModal';
import InputField from '../../../Forms/Input/InputField';
import ActionButton from '../../../Buttons/ActionButton';

import useProductCatalog from '../../../../hooks/productpanel/useProductCatalog';
import useDebounced from '../../../../hooks/useDebounced';

import CategoryButtonSelectable from '../../ProductBrowser/Category/CategoryButtonSelectable';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  branchId: number;
  selectedBrandId: number | null;
  onSelect: (brandId: number | null) => void;
}

const POSBrandFilterModal = ({
  isOpen,
  onClose,
  branchId,
  selectedBrandId,
  onSelect,
}: Props) => {
  const { brands, fetchBrands } = useProductCatalog(branchId);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounced(search.trim().toLowerCase(), 300);

  const [localBrandId, setLocalBrandId] = useState<number | null>(
    selectedBrandId
  );

  // =====================================================
  // 🔹 Load Brands
  // =====================================================

  useEffect(() => {
    if (isOpen && branchId) {
      fetchBrands();
    }
  }, [isOpen, branchId]);

  // =====================================================
  // 🔹 Sync Selection
  // =====================================================

  useEffect(() => {
    setLocalBrandId(selectedBrandId);
  }, [selectedBrandId]);

  // =====================================================
  // 🔹 Reset Search
  // =====================================================

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  // =====================================================
  // 🔹 Filtered Brands
  // =====================================================

  const filteredBrands = useMemo(() => {
    if (!debouncedSearch) {
      return brands;
    }

    return brands.filter((brand) =>
      brand.name.toLowerCase().includes(debouncedSearch)
    );
  }, [brands, debouncedSearch]);

  // =====================================================
  // 🔹 Confirm
  // =====================================================

  const handleConfirm = () => {
    onSelect(localBrandId);

    onClose();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Seleccionar marca"
      maxWidth="max-w-3xl"
    >
      {/* ===================================================== */}
      {/* 🔹 Search */}
      {/* ===================================================== */}

      <InputField
        label="Buscar marca"
        name="search"
        type="text"
        value={search}
        placeholder="Buscar marca..."
        iconKey="search"
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* ===================================================== */}
      {/* 🔹 Brands */}
      {/* ===================================================== */}

      <div
        className="
          mt-5
          max-h-[350px]
          overflow-y-auto
          rounded-xl
          border
          border-stroke
          bg-slate-50
          p-4
          dark:border-strokedark
          dark:bg-boxdark
        "
      >
        <div
          className="
            grid
            grid-cols-1
            gap-3
            sm:grid-cols-2
          "
        >
          <CategoryButtonSelectable
            id={0}
            name="Todas las marcas"
            isSelected={localBrandId === null}
            onClick={() => setLocalBrandId(null)}
            iconName="category"
          />

          {filteredBrands.map((brand) => (
            <CategoryButtonSelectable
              key={brand.id}
              id={brand.id}
              name={brand.name}
              isSelected={localBrandId === brand.id}
              onClick={() => setLocalBrandId(brand.id)}
              iconName="category"
            />
          ))}
        </div>

        {filteredBrands.length === 0 && (
          <div
            className="
              py-10
              text-center
              text-sm
              text-body
            "
          >
            No se encontraron marcas.
          </div>
        )}
      </div>

      {/* ===================================================== */}
      {/* 🔹 Actions */}
      {/* ===================================================== */}

      <div
        className="
          mt-6
          flex
          gap-2
        "
      >
        <ActionButton
          label="Cancelar"
          variant="secondary"
          onClick={onClose}
        />

        <ActionButton
          label="Seleccionar"
          variant="primary"
          onClick={handleConfirm}
        />
      </div>
    </FormModal>
  );
};

export default POSBrandFilterModal;
